import { CsvExportModule } from '@ag-grid-community/csv-export';
import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { AgGridColumnDef, getActivityTypeCodeSelectOptions } from '@tassat/common-definitions';
import React from 'react';

import { getCsvExportParams } from 'Lib/agGridHelper';
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { formatEpochDate } from 'Lib/stringHelpers';
import { formatActivitiesExport, formatActivitiesExportHeaders, getMappedColumn } from 'SharedComponents/activities/activitiesGridOptions';
import ExternalFilter from 'SharedComponents/agGrid/filters/externalFilter';
import ServerSort from 'SharedComponents/agGrid/serverSort';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import ExternalFilterHeaderComponent from 'SharedComponents/cellRenderComponent/externalFilterHeaderComponent';

import type { FirmActivityLogsAgGridProps } from '.';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/simpleTableStyle.css';

export function FirmActivityLogsAgGrid (props: FirmActivityLogsAgGridProps) {
    const {
        activities,
        exportData,
        setExportData,
        firm
    } = props;

    const [ gridApi, onGridReady ] = useAgGrid();

    React.useEffect(() => {
        if (exportData) {
            setExportData(false);
            if (gridApi) {
                gridApi.api.exportDataAsCsv(getCsvExportParams(`Lynq_Platform_${firm.Name}_Activity_Log`, {
                    formattingCallback: formatActivitiesExport,
                    headerFormattingCallback: formatActivitiesExportHeaders
                }));
            }
        }
    }, [exportData]);

    const firmActivityLogsColumnDefs: AgGridColumnDef[] = React.useMemo(() => ([
        {
            field: 'AuditDateTime',
            headerName: 'Date/Time',
            valueFormatter: (params) => formatEpochDate(params.data.AuditDateTime),
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'serverSort',
            filterParams: {
                getMappedColumn,
                defaultSortField: true,
                defaultSortDirection: 'DESC'
            },
            menuTabs: ['filterMenuTab'],
            maxWidth: 200,
            flex: 2
        },
        {
            field: 'RequestId',
            headerName: 'Request ID',
            valueGetter: (params) => params.data.RequestId || '',
            menuTabs: [],
            maxWidth: 250,
            flex: 2
        },
        {
            field: 'RequesterUsername',
            headerName: 'Requester Username',
            valueGetter: (params) => params.data.RequesterUsername || '-',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 3
        },
        {
            field: 'Username',
            headerName: 'Username',
            valueGetter: (params) => params.data.Username || '-',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text'
            },
            menuTabs: ['filterMenuTab'],
            flex: 3
        },
        // hidden type column for export
        {
            field: 'ActivityType',
            headerName: 'Type',
            hide: true
        },
        {
            field: 'Description',
            headerName: 'Type - Description',
            valueFormatter: ({ data }) => `${data.ActivityType} - ${data.Description}`,
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'select',
                selectOptions: 'activityCodeSelectOptions'
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        }

    ]), []);

    const activityCodeSelectOptions = React.useMemo(
        () => getActivityTypeCodeSelectOptions(), []
    );
    React.useEffect(() => {
        gridApi?.api.refreshHeader();
    }, [gridApi]);

    return (
        <div
            className='ag-theme-material'
            id='firmWalletsGrid'
            style={{ height: '100%' }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={firmActivityLogsColumnDefs}
                columnMenu='legacy'
                components={{
                    dateCellRenderer: DateCellRender,
                    externalFilterHeader: ExternalFilterHeaderComponent,
                    externalFilter: ExternalFilter,
                    serverSort: ServerSort
                }}
                context={{ activityCodeSelectOptions }}
                defaultColDef={{
                    resizable: true,
                    filter: false,
                    sortable: false
                }}
                enableCellTextSelection={true}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                modules={[CsvExportModule]}
                onGridReady={onGridReady}
                rowData={activities}
                rowHeight={38}
                suppressCellFocus={true}
                suppressContextMenu={true}
            />
        </div>
    );
}